import React, { useEffect, useRef, useState } from "react";
import { Globe } from "lucide-react";

const GoogleTranslate = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const processedNodes = useRef(new WeakSet());
  const observerRef = useRef(null);
  const dropdownRef = useRef(null);

  const languages = [
    { code: "en", name: "En", fullName: "English" },
    { code: "hi", name: "हिं", fullName: "Hindi" },
    { code: "bn", name: "বাং", fullName: "Bengali" },
    { code: "te", name: "తె", fullName: "Telugu" },
    { code: "mr", name: "मरा", fullName: "Marathi" },
    { code: "ta", name: "த", fullName: "Tamil" },
    { code: "ur", name: "اُر", fullName: "Urdu" },
    { code: "gu", name: "ગુ", fullName: "Gujarati" },
    { code: "kn", name: "ಕ", fullName: "Kannada" },
    { code: "ml", name: "മ", fullName: "Malayalam" },
    { code: "or", name: "ଓ", fullName: "Odia" },
    { code: "pa", name: "ਪੰ", fullName: "Punjabi" },
  ];

  useEffect(() => {
    const wrapNumbersWithNoTranslate = (rootElement = document.body) => {
      const textNodes = [];
      const treeWalker = document.createTreeWalker(
        rootElement,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            if (processedNodes.current.has(node) ||
              node.parentNode?.closest('.notranslate, .goog-te-spinner-pos, .recharts-wrapper, .recharts-surface, .recharts-layer, svg')) {
              return NodeFilter.FILTER_REJECT;
            }
            return node.nodeValue.trim().length > 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
          }
        },
        false
      );

      let node;
      while ((node = treeWalker.nextNode())) {
        textNodes.push(node);
      }

      textNodes.forEach((textNode) => {
        if (textNode.parentNode && textNode.isConnected && !processedNodes.current.has(textNode)) {
          if (textNode.parentNode.closest('.recharts-wrapper, .recharts-surface, svg')) {
            return;
          }
          
          const originalText = textNode.nodeValue;
          const regex = /(\$?\d{1,3}(?:[.,]\d{3})*(?:[.,]\d+)?%?|\d+(?:\.\d+)?%|\d+\/\d+|\d+:\d+(?::\d+)?)/g;

          if (regex.test(originalText)) {
            const parts = originalText.split(regex);
            const fragment = document.createDocumentFragment();

            parts.forEach((part) => {
              if (part.match(regex)) {
                const span = document.createElement("span");
                span.className = "notranslate";
                span.textContent = part;
                fragment.appendChild(span);
              } else if (part) {
                fragment.appendChild(document.createTextNode(part));
              }
            });

            processedNodes.current.add(textNode);
            textNode.parentNode.replaceChild(fragment, textNode);
          }
        }
      });
    };

    const addGoogleTranslateScript = () => {
      if (!document.querySelector("#google-translate-script")) {
        const script = document.createElement("script");
        script.id = "google-translate-script";
        script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
        script.onerror = () => {
          console.error("Failed to load Google Translate script");
          setIsLoaded(true);
        };
        document.head.appendChild(script);
      }
    };

    const setupMutationObserver = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new MutationObserver((mutations) => {
        let shouldProcess = false;

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.TEXT_NODE ||
                (node.nodeType === Node.ELEMENT_NODE && !node.closest('.goog-te-spinner-pos'))) {
                shouldProcess = true;
              }
            });
          }
        });

        if (shouldProcess) {
          setTimeout(() => wrapNumbersWithNoTranslate(), 100);
        }
      });

      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
      });
    };

    window.googleTranslateElementInit = () => {
      if (window.google?.translate?.TranslateElement) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: "en,hi,bn,te,mr,ta,ur,gu,kn,ml,or,pa,as,mai,sd,ne,sa,ks,kok,doi,mni,sat,brx",
          },
          "google_translate_element"
        );

        setTimeout(() => {
          setIsLoaded(true);
          wrapNumbersWithNoTranslate();
        }, 1000);
      }
    };

    const initialDelay = setTimeout(() => {
      wrapNumbersWithNoTranslate();
      addGoogleTranslateScript();
      setupMutationObserver();
    }, 500);

    const handlePageLoad = () => {
      setTimeout(() => wrapNumbersWithNoTranslate(), 100);
    };

    if (document.readyState === 'complete') {
      handlePageLoad();
    } else {
      window.addEventListener('load', handlePageLoad);
    }

    const fallbackTimeout = setTimeout(() => {
      setIsLoaded(true);
    }, 3000);

    return () => {
      clearTimeout(initialDelay);
      clearTimeout(fallbackTimeout);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('load', handlePageLoad);

      if (window.googleTranslateElementInit) {
        delete window.googleTranslateElementInit;
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    setIsOpen(false);
    
    // Trigger Google Translate
    const select = document.querySelector(".goog-te-combo");
    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event("change"));
    }
  };

  const currentLanguage = languages.find(lang => lang.code === selectedLanguage) || languages[0];

  return (
    <div className="relative">
      <style>{`
        /* Hide all Google Translate UI elements */
        #google_translate_element,
        .goog-te-banner-frame,
        .goog-te-spinner-pos,
        .goog-te-spinner,
        .goog-tooltip,
        .goog-tooltip-content,
        .goog-te-balloon-frame,
        .goog-te-balloon,
        #goog-gt-tt,
        .goog-te-ftab,
        .goog-te-ftab-float,
        div[id^="goog-gt-"],
        .goog-te-spinner-animation,
        div[class*="VIpgJd-ZVi9od-aZ2wEe"],
        svg[class*="VIpgJd-ZVi9od-aZ2wEe"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        
        /* Hide top frame */
        body {
          top: 0 !important;
        }
        
        .goog-te-banner-frame {
          display: none !important;
        }
        
        .skiptranslate {
          display: none !important;
        }
      `}</style>
      
      {/* Hidden Google Translate Element */}
      <div id="google_translate_element" style={{ display: 'none' }} />
      
      {/* Custom UI */}
     <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-3 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-200 notranslate"
        >
          <Globe className="w-4 h-4 text-gray-700" strokeWidth={1.5} />
          <span className="text-sm font-medium text-gray-900">
            {currentLanguage.name}
          </span>
          <svg
            className={`w-5 h-5 text-gray-700 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-full right-0 mt-2 w-40 bg-[white] rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 notranslate">
            <div className="max-h-80 overflow-y-auto">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors duration-150 flex items-center justify-between ${
                    selectedLanguage === lang.code ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="text-base text-gray-900">{lang.fullName}</span>
                  <span className="text-lg font-medium text-gray-600">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleTranslate;